import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans } from '@lingui/macro';
import _ from 'lodash';
import moment from 'moment-timezone';
import { Button } from '@material-ui/core';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useFindById } from '../../../services/FormQueryService';
import useSession from '../../../stores/SessionStore';
import { useIsPgm } from '../../../stores/EnvironmentStore';
import format, { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { NgpLabelValue } from '../../form/NgpLabelValue';
import {
    shouldDisableEmail,
    shouldDisableName,
    shouldDisableTimeZone,
    shouldDisableUsername,
    shouldShowSourceField,
} from './UserAdminHelper';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import {
    NOTIFICATIONS_FIELD,
    AGENT_HEARTBEAT_FIELD,
    AGENT_HEARTBEAT_NOTIFICATIONS_FIELD,
    DARK_MODE_FIELD,
    MERIDIEM_TIME_FIELD,
    FIRST_NAME_FIELD,
    FULL_NAME_FIELD,
    LAST_NAME_FIELD,
    TIME_ZONE_FIELD,
} from './UserConstants';
import NgpToggle from '../../form/NgpToggle';
import { useEntityIdParam } from '../AdminLinkUtils';
import UserDetailSection from './UserDetailSection';

const pageId = 'userProfile';
const baseUrl = '/ipm/v1/admin/usersProfile/';
const defaultValues = {
    [TIME_ZONE_FIELD]: moment.tz.guess(),
    [NOTIFICATIONS_FIELD]: {
        [AGENT_HEARTBEAT_FIELD]: false,
    },
};

const disableStatusAndRole = true;

function buildFullUserName(response) {
    const namePartsSeparator = ' ';
    const nameParts = [];
    if (!response) {
        return '';
    }

    nameParts.push(_.trim(_.get(response, FIRST_NAME_FIELD, '')));
    nameParts.push(_.trim(_.get(response, LAST_NAME_FIELD, '')));

    return nameParts.join(namePartsSeparator);
}

export default function UserProfileDetail() {
    const recordId = useEntityIdParam();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, setError, handleSubmit } = methods;
    const functionBreadCrumbs = [];
    const [, sessionActions] = useSession();
    const [showSourceField, setShowSourceField] = useState(false);
    const [disableName, setDisableName] = useState(false);
    const [disableUsername, setDisableUsername] = useState(false);
    const [disableTimeZone, setDisableTimeZone] = useState(false);
    const [disableEmail, setDisableEmail] = useState(false);
    const [isPgm] = useIsPgm();
    const formSaveService = useFormSaveService();

    /**
     * Post handling of data coming back from the server
     * @param response
     * @returns {*}
     */
    const postFindByIdHandler = (response) => {
        setShowSourceField(shouldShowSourceField(response));
        setDisableName(shouldDisableName(response));
        setDisableUsername(shouldDisableUsername(response));
        setDisableTimeZone(shouldDisableTimeZone(response));
        setDisableEmail(shouldDisableEmail(response));

        _.set(response, FULL_NAME_FIELD, buildFullUserName(response));
        _.defaults(response, defaultValues);

        return response;
    };

    const { initialized, finding, formData, resetForm } = useFindById({
        url: baseUrl,
        recordId,
        postFindByIdHandler,
        resetFunc: reset,
        defaultValues,
    });
    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, FULL_NAME_FIELD);

    const handleFormSubmit = (dataToSave = {}) => {
        return formSaveService
            .save({
                url: baseUrl,
                recordId: formData._id,
                dataToSave: _.extend(dataToSave, { _id: formData._id }),
                setError,
            })
            .then((response) => {
                sessionActions.setUser(response);
                resetForm();
                return response;
            });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={pageId}
            actions={
                <Actions>
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm submitFunction={handleSubmitFunction} initialized={initialized} loading={finding || saving}>
                    <FormSection title={<Trans>User Detail</Trans>}>
                        <UserDetailSection
                            showSourceField={showSourceField}
                            disableStatusAndRole={disableStatusAndRole}
                            disableName={disableName}
                            disableEmail={disableEmail}
                            disableUsername={disableUsername}
                            disableTimeZone={disableTimeZone}
                        />

                        <NgpToggle fieldName={DARK_MODE_FIELD} label={<Trans>Use Dark Mode</Trans>} />

                        <NgpToggle
                            fieldName={MERIDIEM_TIME_FIELD}
                            inverted
                            label={<Trans>24 hour time display</Trans>}
                        />
                    </FormSection>

                    {!isPgm && (
                        <FormSection title={<Trans>System Notifications</Trans>}>
                            <NgpToggle
                                fieldName={AGENT_HEARTBEAT_NOTIFICATIONS_FIELD}
                                label={<Trans>Enable nPoint Online/Offline Emails</Trans>}
                            />
                        </FormSection>
                    )}

                    {formData.lastLoginSuccess && (
                        <NgpLabelValue
                            dataLabel="Last successful login"
                            dataValue={format(formData.lastLoginSuccess, { type: FORMATTER_TYPES.DATETIME })}
                        />
                    )}

                    {formData.lastLoginFail && (
                        <NgpLabelValue
                            dataLabel="Last failed login"
                            dataValue={format(formData.lastLoginFail, { type: FORMATTER_TYPES.DATETIME })}
                        />
                    )}
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
